import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { Sync as SyncIcon } from '@material-ui/icons';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { App, Booking, HELP_URL } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { Alert } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';

import LockedSlotReminderModal from '../components/LockedSlotReminderModal';
import OverviewHeader from '../components/OverviewHeader';
import TeeTimeCard from './TeeTimeCard';

const USER_OPTION_NAME = 'showPlanning';

class BookingResult extends React.Component {
  constructor(props) {
    super(props);

    const { user, search, account, golf } = props;

    this.redirect = false;
    if (search.golf === null || search.date === null || search.course === null) {
      this.redirect = true;
      return;
    }

    this.teeTimes = [];
    this.anonymousAllowed = false;
    this.accountMatch = App.functions.match(account, golf);
    this.isMember = App.functions.isMember(account, golf);

    // Golf options
    const bookingOptions = golf.options?.booking || {};
    this.slotLockEnabled = !!bookingOptions.slotLockEnabled;
    this.showOthersOnTimeslot = !!(this.isMember || bookingOptions.showOthersOnTimeslot);
    this.showAvailabilityOnTimeslot = !!(this.isMember || bookingOptions.showAvailabilityOnTimeslot);

    const showPlanning = this.isMember && user.options && user.options[USER_OPTION_NAME];

    this.state = {
      // UI
      error: null,
      loading: true,
      showPlanning,
      isLockModalOpen: false,
      isNoPaymentMethodModalOpen: false,
      // Data
      reasons: [],
      schedule: {},
      locations: [],
      // Alertes
      infoAlert: null,
      dailyNote: null,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = (force = false) => {
    if (this.redirect) return;

    const { search } = this.props;

    this.setState({
      error: null,
      loading: true,
      reasons: [],
      schedule: {},
      locations: [],
      ...(force && { infoAlert: null }),
    });

    const params = {
      date: search.date,
      golfId: search.golf.id,
      courseId: search.course.id,
      courseType: search.course.type,
      players: search.players.map((pl) => pl.reference),
    };

    if (Object.keys(search.accessories).length) {
      params.accessoryIds = Object.keys(search.accessories);
    }

    // Get schedules, course + accessories
    Booking.api
      .getSchedules(apolloClient, params)
      .then(([{ courseSchedule }, { accessoriesSchedule }]) => {
        const schedules = {};
        const locations = [];

        const { reasons, dailyNote, anonymousAllowed } = courseSchedule;
        this.anonymousAllowed = anonymousAllowed;

        schedules.course = Booking.functions.processSchedule('course', courseSchedule.teetimes);
        if (accessoriesSchedule) {
          schedules.accessories = Booking.functions.processSchedule('accessories', accessoriesSchedule);
        } else {
          schedules.accessories = {};
        }
        const detailedSchedule = Booking.functions.getDetailedSchedule(schedules, {
          accessories: search.accessories,
          players: search.players,
        });
        this.teeTimes = Object.keys(detailedSchedule).sort((a, b) => a - b);

        if (detailedSchedule) {
          Object.values(detailedSchedule).forEach(({ others }) => {
            if (others) {
              others.forEach((other) => {
                if (other && other.location) {
                  // Fix: pas confiance dans l'id location
                  if (!locations.find((l) => l.name === other.location.name)) {
                    locations.push(other.location);
                  }
                }
              });
            }
          });
        }
        this.setState({
          reasons,
          dailyNote,
          locations,
          loading: false,
          schedule: detailedSchedule,
        });
      })
      .catch((e) => {
        this.setState({
          loading: false,
          error: e.message,
        });
      });
  };

  async goToCart(time) {
    const { history } = this.props;

    // Départ payant sans aucun moyen de paiement proposable (règle des 20 min sur un
    // golf qui n'ouvre que la carte) : on arrête ici, AVANT de poser le verrou de
    // créneau. Bloquer un créneau que le golfeur ne pourra pas payer le rendrait
    // indisponible au golf pendant toute la durée du verrou, au pire moment.
    // Une alerte en tête de page ne conviendrait pas : la liste des départs couvre
    // toute la journée, le golfeur clique souvent un créneau situé bien plus bas, et
    // le message s'afficherait hors de son champ de vision. Le bouton paraîtrait
    // alors inerte, c'est-à-dire exactement le symptôme que ce correctif supprime.
    if (this.hasNoPaymentMethodForSlot(time)) {
      this.setState({ isNoPaymentMethodModalOpen: true });
      return;
    }

    // Si l'option de blocage est activé
    if (this.slotLockEnabled) {
      const lockResult = await this.lockSlot(time);
      if (!lockResult.success) return;
    }

    this.hydrateBooking(time);
    history.push('/booking/cart');
  }

  /* Le panier applique la même règle sur le total définitif : ce contrôle-ci est là
   * pour éviter un verrou inutile, pas pour s'y substituer. */
  hasNoPaymentMethodForSlot(time) {
    const { account, golf, search } = this.props;
    const { schedule } = this.state;

    const slot = schedule && schedule[time];
    if (!slot) return false;

    return Booking.functions.hasNoPaymentMethodForSlot(App.functions.getPaymentMethods(account, golf, 'booking'), {
      time,
      date: search.date,
      price: slot.total?.price,
    });
  }

  async lockSlot(time) {
    const { golf, search } = this.props;

    const formattedTime = Booking.functions.formatTime(time);

    try {
      const {
        lockedSlot,
        success: lockSuccess,
        serverTimestamp: serverNow,
      } = await Booking.api.lockSlot(masterClient, {
        time: formattedTime,
        golfId: golf.id,
        date: search.date,
        courseId: search.course.id,
      });

      // Si un créneau est déjà bloqué, on le met dans le store
      if (lockedSlot) {
        const clientNow = Math.floor(Date.now() / 1000);
        const timeOffset = serverNow - clientNow;

        this.props.setLockedSlot({
          golfId: search.golf.id,
          ...lockedSlot,
          serverTimeOffset: timeOffset,
        });
      }

      // Lock impossible
      if (lockSuccess === false) {
        // Si créneau déjà bloqué, on affiche la modal de rappel
        if (lockedSlot) {
          this.setState({ isLockModalOpen: true });
        } else {
          // Sinon, le créneau est déjà pris. On actualise les dispos !
          this.setState({
            infoAlert: `Le créneau de ${formattedTime} est en cours de réservation par un autre utilisateur.`,
          });
          this.load();
        }
      } else {
        this.setState({ infoAlert: null });
      }

      return { success: lockSuccess };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  hydrateBooking = (time) => {
    const { accountMatch } = this;
    const { schedule } = this.state;
    const { golf, account, search } = this.props;

    const { firstname, lastname, email, phone, covidCertificate } = account;

    const booking = schedule[time];
    booking.golf = search.golf;
    booking.date = search.date;
    booking.course = search.course;
    booking.accountId = account.id;
    // option
    booking.anonymousAllowed = this.anonymousAllowed;
    // devise
    booking.total.currency = golf.currency;

    // account =/= players[0]
    booking.owner = {
      email,
      phone,
      name: `${firstname} ${lastname}`,
      covidCertificate,
      tid: accountMatch && accountMatch.reference,
    };

    this.props.setBooking(booking);
  };

  handleShowPlanning = () => {
    this.setState(
      (prevState) => ({ showPlanning: !prevState.showPlanning }),
      () =>
        App.api.setUserOption(apolloClient, {
          name: USER_OPTION_NAME,
          value: this.state.showPlanning,
        }),
    );
  };

  renderDailyNote() {
    const { dailyNote } = this.state;

    if (!dailyNote) return null;

    return (
      <Alert variant='default'>
        {dailyNote.split('\\n').map((line, i) => (
          <Fragment key={i}>
            {line}
            <br />
          </Fragment>
        ))}
      </Alert>
    );
  }

  renderUnavailableReasons() {
    const { reasons } = this.state;
    const {
      search: { players },
    } = this.props;

    if (reasons.length === 0) return null;

    const reasonsByPlayer = reasons.reduce((acc, { player, code }) => {
      if (typeof acc[player] === 'undefined') {
        acc[player] = [];
      }
      acc[player].push(code);
      return acc;
    }, {});

    return (
      <Alert variant='error'>
        {Object.keys(reasonsByPlayer).map((ref) => {
          const playerOnError = players.find((p) => p.reference === ref) || {
            firstname: 'Joueur',
            lastname: 'anonyme',
          };

          return (
            <Fragment key={ref}>
              <strong>{`${playerOnError.firstname} ${playerOnError.lastname}`}</strong>
              <br />
              {reasonsByPlayer[ref].map((code, i) => (
                <span key={i}>
                  {i18n.t(code)}
                  <br />
                </span>
              ))}
            </Fragment>
          );
        })}
      </Alert>
    );
  }

  renderScheduleHeader() {
    const { locations, showPlanning } = this.state;

    if (!this.isMember || this.teeTimes.length === 0) return <br />;

    return (
      <Box mb={1} display='flex' alignItems='center' flexDirection='row'>
        {locations.length > 0 && (
          <Box flex={1}>
            {locations.map((l) => (
              <Typography inline variant='caption' key={l.id}>
                <b>{`(${l.name[0]})`}</b> {l.name}
              </Typography>
            ))}
          </Box>
        )}
        <Box flex={1} textAlign='end'>
          <FormControlLabel
            label='Afficher le planning'
            control={<Switch checked={showPlanning} onChange={this.handleShowPlanning} />}
          />
        </Box>
      </Box>
    );
  }

  renderEmpty() {
    return (
      <Box textAlign='center'>
        <Typography paragraph align='center' variant='subtitle1'>
          {"Aucun créneau n'est disponible avec vos critères."}
        </Typography>
        <Button color='secondary' variant='contained' onClick={() => this.props.history.goBack()}>
          {'Retour'}
        </Button>
      </Box>
    );
  }

  renderSchedule() {
    const { schedule, showPlanning } = this.state;

    if (this.teeTimes.length === 0) return this.renderEmpty();

    return (
      <Grid container spacing={24} justify='center'>
        {this.teeTimes.map((time, key) => (
          <TeeTimeCard
            key={key}
            data={schedule[time]}
            showPlanning={showPlanning}
            onBook={() => this.goToCart(time)}
            displayFlags={{
              showOthers: this.showOthersOnTimeslot,
              showAvailability: this.showAvailabilityOnTimeslot,
              showOtherPlayerLocations: this.isMember,
            }}
          />
        ))}
      </Grid>
    );
  }

  renderRefreshButton() {
    if (this.teeTimes.length === 0) return null;

    return (
      <Box display='flex' marginBottom={2} justifyContent='center'>
        <Button variant='contained' color='secondary' onClick={() => this.load(true)}>
          {'Rafraichir'} <SyncIcon style={{ marginLeft: 8 }} />
        </Button>
      </Box>
    );
  }

  renderInfoAlert() {
    const { infoAlert } = this.state;

    if (!infoAlert) return null;

    return <Alert variant='info'>{infoAlert}</Alert>;
  }

  renderLoader() {
    return (
      <Box display='flex' justifyContent='center'>
        <CircularProgress size={24} color='secondary' />
      </Box>
    );
  }

  renderLockedSlotReminderModal() {
    const { isLockModalOpen } = this.state;

    if (!this.slotLockEnabled) return null;

    const closeModal = () => this.setState({ isLockModalOpen: false });

    return <LockedSlotReminderModal open={isLockModalOpen} onClose={closeModal} onRelease={closeModal} />;
  }

  /* Départ payant, plus aucun moyen de paiement sous 20 minutes. Modale et non
   * bandeau : le refus doit être vu, quelle que soit la position de défilement. */
  renderNoPaymentMethodModal() {
    const { isNoPaymentMethodModalOpen } = this.state;

    return (
      <Dialog fullWidth maxWidth='xs' open={isNoPaymentMethodModalOpen}>
        <DialogTitle>{'Réservation impossible en ligne'}</DialogTitle>
        <DialogContent>
          <Typography variant='body1'>{i18n.t('errors.booking.no_payment_method_too_close')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color='secondary' variant='contained' onClick={() => this.setState({ isNoPaymentMethodModalOpen: false })}>
            {'Fermer'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    if (this.redirect) {
      return <Redirect to='/booking' />;
    }

    const { search } = this.props;
    const { error, loading } = this.state;

    const playersCount = search.players.length;
    const accessoriesCount = search.accessoriesTotalQty;

    return (
      <Screen
        error={error}
        title={'Réserver'}
        onRetry={this.load}
        onBackPress={() => this.props.history.goBack()}
        helpURL={`${HELP_URL}/r%C3%A9server#h.p_KPrQp8TPZy75`}
      >
        <OverviewHeader
          date={search.date}
          course={search.course}
          playersCount={playersCount}
          accessoriesCount={accessoriesCount}
        />
        {loading ? (
          this.renderLoader()
        ) : (
          <>
            {this.renderInfoAlert()}
            {this.renderDailyNote()}
            {this.renderUnavailableReasons()}
            {this.renderRefreshButton()}
            {this.renderScheduleHeader()}
            {this.renderSchedule()}
          </>
        )}
        {this.renderLockedSlotReminderModal()}
        {this.renderNoPaymentMethodModal()}
      </Screen>
    );
  }
}
const mapStateToProps = ({ app: { golf, account, user }, bookingData, bookingSearch }) => ({
  golf,
  user,
  account,
  data: bookingData,
  search: bookingSearch,
});

export default connect(mapStateToProps, Booking.actions)(BookingResult);
